import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { AlertCircle, CheckCircle, Loader2, Send } from "lucide-react";
import { cn } from "../lib/utils";

const formSchema = z.object({
  voornaam: z.string().trim().min(1, "Vul je voornaam in").max(100, "Maximaal 100 tekens"),
  achternaam: z.string().trim().min(1, "Vul je achternaam in").max(100, "Maximaal 100 tekens"),
  email: z
    .string()
    .trim()
    .min(1, "Vul je e-mailadres in")
    .email("Vul een geldig e-mailadres in")
    .max(255, "Maximaal 255 tekens"),
  telefoon: z
    .string()
    .trim()
    .regex(/^$|^[+0-9][0-9\s\-()]{7,19}$/, "Vul een geldig telefoonnummer in"),
  adres: z.string().trim().max(200, "Maximaal 200 tekens"),
  onderwerp: z.string().trim().max(150, "Maximaal 150 tekens"),
  bericht: z
    .string()
    .trim()
    .min(10, "Vertel iets meer over je project (minimaal 10 tekens)")
    .max(1000, "Maximaal 1000 tekens"),
});

type FormValues = z.infer<typeof formSchema>;
type FieldName = keyof FormValues;

const fieldOrder: FieldName[] = [
  "voornaam",
  "achternaam",
  "email",
  "telefoon",
  "adres",
  "onderwerp",
  "bericht",
];

const initialValues: FormValues = {
  voornaam: "",
  achternaam: "",
  email: "",
  telefoon: "",
  adres: "",
  onderwerp: "",
  bericht: "",
};

const labelClasses = "text-sm font-medium text-foreground";

// text-base (16px) voorkomt dat iOS Safari automatisch inzoomt bij focus.
const baseInputClasses =
  "w-full rounded-md border bg-background px-4 py-3 text-base text-foreground placeholder:text-muted-foreground transition-colors focus:outline-none focus:ring-1";

function inputClasses(hasError: boolean) {
  return cn(
    baseInputClasses,
    hasError
      ? "border-destructive focus:border-destructive focus:ring-destructive"
      : "border-input focus:border-primary focus:ring-primary",
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      className="flex items-center gap-1.5 text-xs font-medium text-destructive"
    >
      <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {message}
    </p>
  );
}

/** Velden die automatisch gevuld mogen worden (bijv. door de aanbouw-calculator). */
export type ContactFormPrefill = {
  onderwerp?: string;
  bericht?: string;
};

export function ContactForm({ prefill }: { prefill?: ContactFormPrefill } = {}) {
  // Values are mirrored in state via onChange (the inputs stay uncontrolled
  // to keep SSR HTML and hydration identical); formKey remounts the form to
  // clear the fields after a successful submit.
  const [values, setValues] = useState<FormValues>(() => ({
    ...initialValues,
    ...(prefill?.onderwerp ? { onderwerp: prefill.onderwerp } : {}),
    ...(prefill?.bericht ? { bericht: prefill.bericht } : {}),
  }));
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [formKey, setFormKey] = useState(0);

  // Prefill blijft meelopen met de calculator zolang de bezoeker het veld
  // zelf nog niet heeft aangepast.
  const onderwerpRef = useRef<HTMLInputElement>(null);
  const berichtRef = useRef<HTMLTextAreaElement>(null);
  const editedRef = useRef<Partial<Record<FieldName, boolean>>>({});

  useEffect(() => {
    if (!prefill) return;
    const sync = (
      name: "onderwerp" | "bericht",
      value: string | undefined,
      el: HTMLInputElement | HTMLTextAreaElement | null,
    ) => {
      if (value === undefined || editedRef.current[name]) return;
      if (el) el.value = value;
      setValues((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }));
    };
    sync("onderwerp", prefill.onderwerp, onderwerpRef.current);
    sync("bericht", prefill.bericht, berichtRef.current);
  }, [prefill?.onderwerp, prefill?.bericht, prefill, formKey]);


  const validateField = (name: FieldName, value: string) => {
    const result = formSchema.shape[name].safeParse(value);
    return result.success ? undefined : result.error.issues[0]?.message;
  };

  const handleChange = (name: FieldName, value: string) => {
    editedRef.current[name] = true;
    setValues((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (name: FieldName) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, values[name]),
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(undefined);

    const result = formSchema.safeParse(values);
    if (!result.success) {
      const nextErrors: Partial<Record<FieldName, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as FieldName;
        if (!nextErrors[field]) nextErrors[field] = issue.message;
      }
      setErrors(nextErrors);
      setTouched(
        fieldOrder.reduce(
          (acc, field) => ({ ...acc, [field]: true }),
          {} as Record<FieldName, boolean>,
        ),
      );
      const firstInvalid = fieldOrder.find((field) => nextErrors[field]);
      if (firstInvalid) {
        document.getElementById(firstInvalid)?.focus();
      }
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(result.data),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(
          body.error ?? "Er ging iets mis bij het verzenden. Probeer het opnieuw.",
        );
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Er ging iets mis bij het verzenden. Probeer het opnieuw.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    editedRef.current = {};
    setValues({
      ...initialValues,
      ...(prefill?.onderwerp ? { onderwerp: prefill.onderwerp } : {}),
      ...(prefill?.bericht ? { bericht: prefill.bericht } : {}),
    });

    setErrors({});
    setTouched({});
    setSubmitted(false);
    setFormKey((key) => key + 1);
  };

  const errorFor = (name: FieldName) => (touched[name] ? errors[name] : undefined);

  if (submitted) {
    return (
      <div
        role="status"
        className="rounded-xl border border-primary/20 bg-primary/5 p-8 text-center"
      >
        <CheckCircle className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
        <h3 className="mt-4 font-heading text-xl font-semibold text-foreground">
          Bedankt voor het indienen!
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          We hebben je bericht ontvangen en nemen zo snel mogelijk contact met je op.
        </p>
        <button
          type="button"
          onClick={resetForm}
          className="mt-6 inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
        >
          Nog een bericht sturen
        </button>
      </div>
    );
  }

  return (
    <form key={formKey} onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="voornaam" className={labelClasses}>
            Voornaam
          </label>
          <input
            id="voornaam"
            name="voornaam"
            type="text"
            required
            autoComplete="given-name"
            defaultValue=""
            onChange={(e) => handleChange("voornaam", e.target.value)}
            onBlur={() => handleBlur("voornaam")}
            aria-invalid={errorFor("voornaam") ? true : undefined}
            aria-describedby={errorFor("voornaam") ? "voornaam-error" : undefined}
            className={inputClasses(!!errorFor("voornaam"))}
          />
          <FieldError id="voornaam-error" message={errorFor("voornaam")} />
        </div>
        <div className="space-y-2">
          <label htmlFor="achternaam" className={labelClasses}>
            Achternaam
          </label>
          <input
            id="achternaam"
            name="achternaam"
            type="text"
            required
            autoComplete="family-name"
            defaultValue=""
            onChange={(e) => handleChange("achternaam", e.target.value)}
            onBlur={() => handleBlur("achternaam")}
            aria-invalid={errorFor("achternaam") ? true : undefined}
            aria-describedby={errorFor("achternaam") ? "achternaam-error" : undefined}
            className={inputClasses(!!errorFor("achternaam"))}
          />
          <FieldError id="achternaam-error" message={errorFor("achternaam")} />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="email" className={labelClasses}>
            E-mail
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="jij@voorbeeld.nl"
            defaultValue=""
            onChange={(e) => handleChange("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            aria-invalid={errorFor("email") ? true : undefined}
            aria-describedby={errorFor("email") ? "email-error" : undefined}
            className={inputClasses(!!errorFor("email"))}
          />
          <FieldError id="email-error" message={errorFor("email")} />
        </div>
        <div className="space-y-2">
          <label htmlFor="telefoon" className={labelClasses}>
            Telefoon <span className="font-normal text-muted-foreground">(optioneel)</span>
          </label>
          <input
            id="telefoon"
            name="telefoon"
            type="tel"
            autoComplete="tel"
            placeholder="06-12345678"
            defaultValue=""
            onChange={(e) => handleChange("telefoon", e.target.value)}
            onBlur={() => handleBlur("telefoon")}
            aria-invalid={errorFor("telefoon") ? true : undefined}
            aria-describedby={errorFor("telefoon") ? "telefoon-error" : undefined}
            className={inputClasses(!!errorFor("telefoon"))}
          />
          <FieldError id="telefoon-error" message={errorFor("telefoon")} />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="adres" className={labelClasses}>
            Adres <span className="font-normal text-muted-foreground">(optioneel)</span>
          </label>
          <input
            id="adres"
            name="adres"
            type="text"
            autoComplete="street-address"
            defaultValue=""
            onChange={(e) => handleChange("adres", e.target.value)}
            onBlur={() => handleBlur("adres")}
            aria-invalid={errorFor("adres") ? true : undefined}
            aria-describedby={errorFor("adres") ? "adres-error" : undefined}
            className={inputClasses(!!errorFor("adres"))}
          />
          <FieldError id="adres-error" message={errorFor("adres")} />
        </div>
        <div className="space-y-2">
          <label htmlFor="onderwerp" className={labelClasses}>
            Onderwerp <span className="font-normal text-muted-foreground">(optioneel)</span>
          </label>
          <input
            id="onderwerp"
            name="onderwerp"
            type="text"
            ref={onderwerpRef}
            placeholder="Bijv. dakkapel, kozijnen, aanbouw..."
            defaultValue=""

            onChange={(e) => handleChange("onderwerp", e.target.value)}
            onBlur={() => handleBlur("onderwerp")}
            aria-invalid={errorFor("onderwerp") ? true : undefined}
            aria-describedby={errorFor("onderwerp") ? "onderwerp-error" : undefined}
            className={inputClasses(!!errorFor("onderwerp"))}
          />
          <FieldError id="onderwerp-error" message={errorFor("onderwerp")} />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="bericht" className={labelClasses}>
          Bericht
        </label>
        <textarea
          id="bericht"
          name="bericht"
          ref={berichtRef}
          rows={5}
          required
          maxLength={1000}
          placeholder="Typ je bericht hier..."
          defaultValue=""

          onChange={(e) => handleChange("bericht", e.target.value)}
          onBlur={() => handleBlur("bericht")}
          aria-invalid={errorFor("bericht") ? true : undefined}
          aria-describedby={errorFor("bericht") ? "bericht-error" : undefined}
          className={inputClasses(!!errorFor("bericht"))}
        />
        <div className="flex items-start justify-between gap-4">
          <FieldError id="bericht-error" message={errorFor("bericht")} />
          <p className="ml-auto shrink-0 text-xs text-muted-foreground">
            {values.bericht.length}/1000
          </p>
        </div>
      </div>

      {submitError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{submitError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {submitting ? (
          <>
            Verzenden...
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          </>
        ) : (
          <>
            Indienen
            <Send className="h-4 w-4" aria-hidden="true" />
          </>
        )}
      </button>
    </form>
  );
}
